import type { FormFieldDefinition } from '@engage/types';

/**
 * Pure visibility evaluation for forward-only conditional logic.
 * Returns true when the field should render. Always true when no rule.
 *
 * The picker (Task 3 builder) constrains references to *preceding* fields
 * only, so this never needs cycle detection. Defensive: an unknown
 * `field_key` returns true (we show the field rather than hide it on a
 * mistake — fail open).
 */
export function isVisible(
  field: FormFieldDefinition,
  values: Record<string, unknown>
): boolean {
  const rule = field.conditional_logic;
  if (!rule) return true;
  const other = values[rule.field_key];
  if (other === undefined) return true; // referent not present yet → show
  switch (rule.operator) {
    case 'equals':
      return looseEquals(other, rule.value);
    case 'not_equals':
      return !looseEquals(other, rule.value);
    case 'contains':
      if (typeof other === 'string' && typeof rule.value === 'string') {
        return other.toLowerCase().includes(rule.value.toLowerCase());
      }
      if (Array.isArray(other)) return other.includes(rule.value);
      return false;
  }
}

function looseEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
}
